/**
 * Lightweight first-party behaviour analytics (8 JUN 2026).
 *
 * Emits page views, time-on-page (dwell), and navigation transitions to our own
 * `public.analytics_events` Supabase table (RLS: insert-own · admin reads). No
 * third-party — data stays in our DB (152-ФЗ-friendlier). Per-user/identified.
 *
 * Design notes:
 *  - Append-only `supabase.from('analytics_events').insert(...)` (RLS-gated), no
 *    edge function needed — analytics is a simple fire-and-forget write.
 *  - Batched + debounced; flushed on tab hide (`visibilitychange`/`pagehide`).
 *  - NEVER throws into the app: a missing table (pre-migration) or any insert
 *    error is swallowed. Analytics must not affect the product.
 *  - Gated on a known user id (`setAnalyticsUser`). No user → events dropped.
 *  - `sessionId` is per-tab (sessionStorage) so flows can be reconstructed.
 */

import { supabase } from '@/api/supabaseClient';

export type AnalyticsKind = 'page_view' | 'page_leave' | 'nav' | 'sync_storm';

interface AnalyticsRow {
  user_id: string;
  session_id: string;
  kind: AnalyticsKind;
  screen: string;
  detail: string | null;
  from_screen: string | null;
  dwell_ms: number | null;
  ts: string;
}

const FLUSH_DEBOUNCE_MS = 4000;
const MAX_BATCH = 50;
const SESSION_KEY = 'qastudio.v1.analyticsSession';

let ownerId: string | null = null;
let queue: AnalyticsRow[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let cachedSessionId: string | null = null;

function randomId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  // Non-crypto fallback (id only needs to be unique-enough per tab).
  return `s-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
}

function sessionId(): string {
  if (cachedSessionId) return cachedSessionId;
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = randomId();
      sessionStorage.setItem(SESSION_KEY, s);
    }
    cachedSessionId = s;
  } catch {
    cachedSessionId = randomId(); // private mode / no storage
  }
  return cachedSessionId;
}

/** Bind analytics to the current user. Pass null on logout / no session. */
export function setAnalyticsUser(userId: string | null): void {
  if (userId === ownerId) return;
  // Identity changed (login / logout / cross-tab swap): flush what we have for
  // the old user, then switch. Never carry one user's events under another id.
  void flushAnalytics();
  queue = [];
  ownerId = userId;
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushAnalytics();
  }, FLUSH_DEBOUNCE_MS);
}

function enqueue(
  kind: AnalyticsKind,
  screen: string,
  opts: { detail?: string | null; from?: string | null; dwellMs?: number | null } = {},
): void {
  if (!ownerId) return; // only track identified, authed users
  queue.push({
    user_id: ownerId,
    session_id: sessionId(),
    kind,
    screen,
    detail: opts.detail ?? null,
    from_screen: opts.from ?? null,
    dwell_ms: opts.dwellMs ?? null,
    ts: new Date().toISOString(),
  });
  if (queue.length >= MAX_BATCH) void flushAnalytics();
  else scheduleFlush();
}

export function trackView(screen: string, detail?: string | null): void {
  enqueue('page_view', screen, { detail });
}

export function trackLeave(screen: string, detail: string | null, dwellMs: number): void {
  // Drop absurd dwell (clock skew / very long idle) to keep aggregates sane.
  if (!Number.isFinite(dwellMs) || dwellMs < 0 || dwellMs > 6 * 60 * 60 * 1000) return;
  enqueue('page_leave', screen, { detail, dwellMs: Math.round(dwellMs) });
}

export function trackNav(from: string, to: string, detail?: string | null): void {
  enqueue('nav', to, { from, detail });
}

/**
 * Record a progress-sync storm (ADR-019 · P4 detector). Fire-and-forget insert
 * into `analytics_events` with the burst count + branch mix in `meta`. Written
 * immediately (not batched) — it's rare and diagnostic. Consent-gated (dropped
 * when no analytics user is bound) and error-swallowing, exactly like the rest
 * of analytics: telemetry must never affect the product or throw.
 */
export function trackSyncStorm(count: number, branches: string[]): void {
  if (!ownerId) return;
  const row = {
    user_id: ownerId,
    session_id: sessionId(),
    kind: 'sync_storm' as const,
    screen: 'sync',
    detail: null,
    from_screen: null,
    dwell_ms: count,
    ts: new Date().toISOString(),
    meta: { count, branches },
  };
  void (async () => {
    try {
      await supabase.from('analytics_events').insert(row);
    } catch {
      /* best-effort · drop */
    }
  })();
}

/** Best-effort flush. Swallows all errors — analytics never breaks the app. */
export async function flushAnalytics(): Promise<void> {
  if (!ownerId || queue.length === 0) return;
  const batch = queue;
  queue = [];
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  try {
    // Pre-migration table-missing or RLS error: drop silently (don't requeue
    // forever). One lost batch is acceptable for behaviour analytics — never
    // surfaced to the user, never throws.
    await supabase.from('analytics_events').insert(batch);
  } catch {
    /* network/other · best-effort, drop */
  }
}

/** Test seam · reset module state between cases. */
export function __resetAnalyticsForTests(): void {
  ownerId = null;
  queue = [];
  cachedSessionId = null;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

/** Test seam · inspect the pending queue. */
export function __analyticsQueueForTests(): ReadonlyArray<AnalyticsRow> {
  return queue;
}
